import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { FormControlLabel, Grid, Switch, Tooltip } from "@mui/material"
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { Order } from "../../../../controlKitchenTypes"
import FloatingOrderActionBar from "../../components/FloatingOrderActionBar"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchOrdersByPeriod,
    MerchantProductionTransactions,
    setOrderStatus,
    StandardTransactionResultResponse,
} from "../../productionApi"
import { TableToolbarBanner } from "../../components/TableToolbarBanner"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { StatusChangeModal } from "../../meetingService/components/StatusChangeModal"
import { useExportOrdersToCsv } from "../../components/ExportFunctionOrder"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import StandardTransactionResultResponseModal from "../../components/StandardTransactionResultResponseModal"
import { LoadingDialog } from "../../../../../../shared/components/dialogs/LoadingDialog"
import LoadingLottie from "../../assets/Loading-color-balls-jumping.json"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { faIdCard, faPrint } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../../shared/components/table/TableSummaryBox"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { PaymentMethodCell } from "../../components/PaymentMethodCell"
import { MinimizedStatusButton, StatusButton } from "../../meetingService/components/StatusMenu"
import { FilteredStatuses, MeetingServiceOrderStatus } from "../../meetingService/meetingServiceTypes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import useActiveFiltersCountAccounting from "../../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { PosDeliveryList, DeliveryListMode, PosOrdersDependencies } from "./PosDeliveryList"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"
import { TimePeriodMeetingService, getTimePeriod } from "../../meetingService/meetingServiceTypes"
import { LocalCache, StorageKey } from "../../../../../../shared/cache/LocalCache"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { useDateTime } from "../../../../../localization/useDateTime"
import { OrderActionMenu } from "../../components/OrderActionMenu"

// Enum for orderType filter matching API values
export enum ProductionOrderTypeFilter {
    LUNCH = "LUNCH",
    TAKEAWAY = "TAKEAWAY",
}

const useStyles = makeStyles()(() => ({
    idCardIcon: {
        marginLeft: ".5rem",
        cursor: "pointer",
        color: tailwindPalette.sky[500],
    },
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    deliveryListButton: {
        whiteSpace: "nowrap",
    },
    dateFilter: {
        maxWidth: "11rem",
        ".MuiSelect-select": {
            padding: "0.5rem 1rem",
        },
    },
    filterButtons: {
        paddingTop: 0,
    },
}))

type PosTransactionsTableProps = Readonly<{
    defaultOrderType: ProductionOrderTypeFilter
    isTakeaway?: boolean
}>

export function PosTransactionsTable({ defaultOrderType, isTakeaway = false }: PosTransactionsTableProps) {
    const { classes } = useStyles()
    const [searchParams] = useSearchParams()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [isCancelled, setIsCancelled] = useState(false)
    const [showNotPrintedOrders, setShowNotPrintedOrders] = useState(false)
    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
    const [selectedStatus, setSelectedStatus] = useState<FilteredStatuses | undefined>()

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [deliveryListState, setDeliveryListState] = useState<{
        open: boolean
        mode: DeliveryListMode
    }>({
        open: false,
        mode: "selected",
    })

    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const storageKey = useMemo(() => {
        return defaultOrderType === ProductionOrderTypeFilter.TAKEAWAY
            ? StorageKey.TAKEAWAY_TIME_PERIOD
            : StorageKey.LUNCH_TIME_PERIOD
    }, [defaultOrderType])

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [timePeriod, setTimePeriod] = useState<TimePeriodMeetingService>(() => {
        const saved = LocalCache().get(storageKey)
        if (saved && Object.values(TimePeriodMeetingService).includes(saved as TimePeriodMeetingService)) {
            return saved as TimePeriodMeetingService
        }
        return TimePeriodMeetingService.TODAY
    })

    const [dateType, setDateType] = useState<DateType>(DateType.DELIVERY_DATE)
    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
        merchantId: merchantId.toString(),
    })

    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const todayStart = today.startOf("day")
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate < todayStart ? todayStart : startDate,
        endDate: endDate && endDate < todayStart ? todayStart : endDate,
    })

    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const [orderStatusResponseResult, setOrderStatusResponseResult] = useState<StandardTransactionResultResponse>()
    const [selectedRows, setSelectedRows] = useState<Order[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<Order>>(null)

    // Status menu state
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
    const [activeRow, setActiveRow] = useState<Order | undefined>(undefined)
    const [isFromFloatingBar, setIsFromFloatingBar] = useState(false)

    const { mutateAsync: callSetOrderStatus, isLoading } = useApiMutation(setOrderStatus, {
        async onSuccess(_params, data) {
            setOrderStatusResponseResult(data)
        },
    })

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the merchant searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const orderListDependencies = useMemo(
        () => ({
            ...filtersList,
            merchantId,
            isCancelled,
            period:
                isSearchingByOrderId || isDatePicker || timePeriod === TimePeriodMeetingService.ALL
                    ? undefined
                    : timePeriod,
            startDate:
                !isSearchingByOrderId && isDatePicker ? (dateState.startDate?.toISODate() ?? undefined) : undefined,
            endDate: !isSearchingByOrderId && isDatePicker ? (dateState.endDate?.toISODate() ?? undefined) : undefined,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            dateType,
            orderType: defaultOrderType,
        }),
        [
            filtersList,
            merchantId,
            isCancelled,
            isDatePicker,
            isSearchingByOrderId,
            timePeriod,
            dateType,
            dateState.startDate,
            dateState.endDate,
            defaultOrderType,
        ],
    )

    const orderList = useApiQuery(fetchOrdersByPeriod, {
        queryName: MerchantProductionTransactions.GET_ALL_ORDERS_BY_PERIOD,
        dependencies: orderListDependencies,
        select: (data) => data.orders ?? [],
    })

    // Update time period when navigating from AwaitingApprovalNavItem
    useEffect(() => {
        const periodParam = searchParams.get("period")
        if (periodParam && Object.values(TimePeriodMeetingService).includes(periodParam as TimePeriodMeetingService)) {
            const newPeriod = periodParam as TimePeriodMeetingService
            if (timePeriod !== newPeriod) {
                setTimePeriod(newPeriod)
                setIsDatePicker(false)
                LocalCache().set(storageKey, newPeriod)
            }
        }
    }, [searchParams, timePeriod, storageKey])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(orderList)

    const onStatusClick = (placement: "top" | "bottom") => (e: React.MouseEvent<HTMLButtonElement>, row?: Order) => {
        if (menuPos?.left || menuPos?.top) {
            setMenuPos(null)
            setActiveRow(undefined)
            setIsFromFloatingBar(false)
        } else {
            const rect = e.currentTarget.getBoundingClientRect()
            setActiveRow(row)
            setIsFromFloatingBar(placement === "top")
            setMenuPos((prev) =>
                prev
                    ? null
                    : placement === "bottom"
                      ? { top: rect.bottom, left: rect.right }
                      : { top: rect.top, left: rect.right },
            )
        }
    }

    const allowedOrderIds = useMemo(() => {
        if (!selectedStatus || (!selectedRows && !activeRow)) return []
        const rows = activeRow ? [activeRow] : selectedRows || []
        return rows.map((order) => order.id)
    }, [selectedStatus, selectedRows, activeRow])

    const handleStatusChange = (status: FilteredStatuses) => {
        setSelectedStatus(status as FilteredStatuses)
        setShowStatusChangeModal(true)
        setMenuPos(null)
        setIsFromFloatingBar(false)
    }

    const handleCloseStatusModal = () => {
        setShowStatusChangeModal(false)
        setSelectedStatus(undefined)
        setActiveRow(undefined)
        handleClearSelected()
    }

    const handleStatusClose = () => {
        setMenuPos(null)
        setIsFromFloatingBar(false)
    }

    const columns = useMemo<MRT_ColumnDef<Order>[]>(() => {
        const baseColumns: MRT_ColumnDef<Order>[] = [
            columnFactory.idColumn({
                header: l10n.getString("production-order-number"),
                accessorKey: "id",
            }),
            dateType === DateType.DELIVERY_DATE
                ? columnFactory.datetimeColumn(
                      {
                          header: l10n.getString("production-order-delivery-time"),
                          id: "deliveries[0].deliveryTime",
                          size: COLUMN_SIZE.M,
                          maxSize: COLUMN_SIZE.M,
                      },
                      (row) => row.deliveries[0].deliveryTime,
                  )
                : columnFactory.datetimeColumn(
                      { header: l10n.getString("production-order-time"), size: COLUMN_SIZE.M, maxSize: COLUMN_SIZE.M },
                      (row) => row.created,
                  ),
            columnFactory.nameColumn({
                header: l10n.getString("office-purchase-name"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                {row.original?.customer?.displayName ?? ""}
                                <Tooltip arrow title={row.original.paymentDetails.idCard?.cardNumber}>
                                    {row.original.paymentDetails.idCard?.cardNumber ? (
                                        <FontAwesomeIcon className={classes.idCardIcon} icon={faIdCard} size="lg" />
                                    ) : (
                                        <></>
                                    )}
                                </Tooltip>
                            </>
                        }
                        subtitle1={row.original.customer?.employeeId ?? ""}
                        subtitle2={row.original.customer?.email}
                    />
                ),
            }),
            columnFactory.typeColumn({
                header: l10n.getString("production-order-payment-status"),
                accessorKey: "paymentDetails.method",
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                Cell: ({ row }) => <PaymentMethodCell paymentDetails={row.original.paymentDetails} />,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ]

        if (isTakeaway) {
            // Add organizer column for takeaway orders (sourced from customer object)
            // Remove column for client demo
            // baseColumns.push(
            //     columnFactory.nameColumn({
            //         id: "organizer",
            //         header: "Organizer",
            //         accessorFn: (row) => row.customer?.displayName ?? "",
            //         size: COLUMN_SIZE.M,
            //         Cell: ({ row }) => {
            //             const customer = row.original?.customer
            //             if (!customer) return null
            //             return (
            //                 <StandardTwoLinesCell
            //                     title={customer.displayName}
            //                     subtitle1={customer.email ?? ""}
            //                     subtitle2={customer.phone ?? ""}
            //                 />
            //             )
            //         },
            //     }),
            // )

            // Add status column for takeaway orders
            baseColumns.push(
                columnFactory.statusColumn({
                    id: "status",
                    header: l10n.getString("service-center-admin-organization-resources-table-header-status"),
                    accessorFn: (row) => row.deliveryDetails?.statusDetails?.status,
                    size: 240,
                    minSize: 240,
                    Cell: ({ row }) => {
                        const status = row.original?.deliveryDetails?.statusDetails?.status

                        return (
                            <MinimizedStatusButton
                                onClick={(e) => onStatusClick("bottom")(e, row.original)}
                                status={status}
                                tooltipLabel={row.original?.deliveryDetails?.statusDetails?.message || ""}
                            />
                        )
                    },
                }),
            )
        }

        return baseColumns
    }, [l10n, columnFactory, dateType, classes.idCardIcon, isTakeaway])

    function getHiddenOrderStatuses(): MeetingServiceOrderStatus[] {
        const hiddenStatusesBase = [
            MeetingServiceOrderStatus.NOT_APPROVED,
            MeetingServiceOrderStatus.REJECTED,
            MeetingServiceOrderStatus.DELIVERED,
            MeetingServiceOrderStatus.TO_BE_BOOKED,
            MeetingServiceOrderStatus.BOOKED,
            MeetingServiceOrderStatus.LOCKED,
            MeetingServiceOrderStatus.APPROVED,
        ]

        if (defaultOrderType === ProductionOrderTypeFilter.LUNCH) {
            return [...hiddenStatusesBase, MeetingServiceOrderStatus.PREPARING, MeetingServiceOrderStatus.COMPLETED]
        }

        return hiddenStatusesBase
    }

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const hasNonePrinted = () => {
        if (!orderList.data) {
            return false
        }

        return (
            orderList.data.filter((order) => {
                return order.printDetails?.isPrinted === false
            }).length > 0
        )
    }

    const showAlertBanner = useMemo(() => hasNonePrinted(), [orderList.data])

    const exportOrder = useExportOrdersToCsv({
        data: orderList.data,
        startDate: isDatePicker
            ? (dateState.startDate ?? today)
            : (getTimePeriod(dateTimeFactory, timePeriod)?.start ?? today),
        endDate: isDatePicker
            ? (dateState.endDate ?? today)
            : (getTimePeriod(dateTimeFactory, timePeriod)?.end ?? today),
        showStatusColumn: isTakeaway,
    })

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const handleCloseDeliveryList = () => {
        setDeliveryListState((prev) => ({ ...prev, open: false }))
    }

    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []

    // The delivery list must cover the same period as the table: the picked date range, or the dates the
    // selected time period ("Today", "Tomorrow", ...) resolves to. "All" leaves the dates open.
    const deliveryListPeriod = useMemo(() => {
        if (isDatePicker) return { start: dateState.startDate, end: dateState.endDate }
        if (timePeriod === TimePeriodMeetingService.ALL) return { start: null, end: null }
        if (timePeriod === TimePeriodMeetingService.YESTERDAY) {
            const yesterday = dateTimeFactory.now().minus({ days: 1 })
            return { start: yesterday.startOf("day"), end: yesterday.endOf("day") }
        }
        return getTimePeriod(dateTimeFactory, timePeriod) ?? { start: null, end: null }
    }, [isDatePicker, dateState.startDate, dateState.endDate, timePeriod, dateTimeFactory])

    const posDependencies: PosOrdersDependencies = useMemo(
        () => ({
            merchantId,
            startDeliveryDate: deliveryListPeriod.start?.toISODate(),
            endDeliveryDate: deliveryListPeriod.end?.toISODate(),
            shopIds: filtersList.shopIds?.join(","),
            orderType: defaultOrderType,
        }),
        [merchantId, filtersList, deliveryListPeriod, defaultOrderType],
    )

    return (
        <>
            <Grid className={classes.table}>
                {showAlertBanner && (
                    <TableToolbarBanner
                        showNotPrintedOrders={showNotPrintedOrders}
                        setShowNotPrintedOrders={setShowNotPrintedOrders}
                    />
                )}

                <ApiQueryTable
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onColumnFiltersChange={setColumnFilters}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    // Use grid layout for takeaway to ensure columns respect minSize and don't get cut off
                    layoutMode={isTakeaway ? "grid" : undefined}
                    initialState={{
                        density: "spacious",
                        sorting: [
                            {
                                id: "deliveries[0].deliveryTime",
                                desc: false,
                            },
                        ],
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <OrderActionMenu
                            key={`order-action-menu-${row.original.id}`}
                            order={row.original}
                            orders={
                                tableInstanceRef.current?.getPrePaginationRowModel().rows.map((row) => row.original) ??
                                []
                            }
                            onSuccess={() => orderList.refetch()}
                            closeMenu={closeMenu}
                            orderType={defaultOrderType}
                        />,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter stackClassName={classes.filterButtons} variant="right" table={table}>
                            <LocalizedStrict id="meeting-service-delivery-list">
                                <StandardButton
                                    fullWidth={false}
                                    size="small"
                                    variant="outlined"
                                    className={classes.deliveryListButton}
                                    onClick={() => {
                                        setDeliveryListState({ open: true, mode: "all" })
                                    }}
                                    startIcon={<FontAwesomeIcon icon={faPrint} />}
                                >
                                    Delivery list
                                </StandardButton>
                            </LocalizedStrict>
                            <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                            <StandardExportButton
                                onClick={() => {
                                    exportOrder()
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <TimeOrDatePicker<TimePeriodMeetingService>
                                excludeDateTime={false}
                                isDatePicker={isDatePicker}
                                dateState={dateState}
                                setDateState={setDateState}
                                timePeriodOptions={[...Object.values(TimePeriodMeetingService)]}
                                timePeriod={timePeriod}
                                handleChangeTimePeriod={(newPeriod) => {
                                    setTimePeriod(newPeriod)
                                    LocalCache().set(storageKey, newPeriod)
                                }}
                                handleChangeTimeOrDatePicker={() => setIsDatePicker((prev) => !prev)}
                            />
                            <SelectDateTypeFilter
                                className={classes.dateFilter}
                                dateType={dateType}
                                setDateType={setDateType}
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={orderList.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>
                        </TableSummaryWrap>
                    )}
                    state={{
                        columnFilters,
                        rowSelection,
                    }}
                ></ApiQueryTable>
            </Grid>
            {selectedRows && (
                <FloatingOrderActionBar
                    showPrintLabel={isTakeaway}
                    variant="pos"
                    selectedRows={selectedRows}
                    startDate={dateState.startDate ?? today}
                    endDate={dateState.endDate ?? today}
                    setSelectedRows={setRowSelection}
                    handleClearSelected={handleClearSelected}
                    onStatusClick={onStatusClick("top")}
                />
            )}
            {isTakeaway && (
                <StatusButton
                    placement={Boolean(selectedRows?.length) ? "top" : "bottom"}
                    hiddenStatuses={getHiddenOrderStatuses()}
                    onStatusChange={handleStatusChange}
                    anchorEl={menuPos ?? undefined}
                    handleClose={handleStatusClose}
                    currentStatus={activeRow?.deliveryDetails?.statusDetails?.status}
                />
            )}
            {showStatusChangeModal && (activeRow || selectedRows) && (
                <StandardDialog
                    open={showStatusChangeModal}
                    onClose={handleCloseStatusModal}
                    dialogTitle="Change status"
                    maxWidthProp="lg"
                >
                    <StatusChangeModal
                        setShowUndo={() => {}}
                        onClose={handleCloseStatusModal}
                        selectedStatus={selectedStatus!}
                        allowedOrderIds={allowedOrderIds}
                        orders={activeRow ? [activeRow] : selectedRows!}
                        meetingServiceDependencies={orderListDependencies}
                        isTakeaway={isTakeaway}
                    />
                </StandardDialog>
            )}

            {orderStatusResponseResult && (
                <StandardTransactionResultResponseModal
                    onClose={setOrderStatusResponseResult}
                    data={orderStatusResponseResult}
                />
            )}
            <LoadingDialog loading={isLoading} lottieAnim={LoadingLottie} />

            {deliveryListState.open && (
                <PosDeliveryList
                    open={deliveryListState.open}
                    onClose={handleCloseDeliveryList}
                    orderIds={orderIds}
                    mode={deliveryListState.mode}
                    dependencies={posDependencies}
                />
            )}

            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            >
                <FormControlLabel
                    control={<Switch checked={isCancelled} onClick={() => setIsCancelled(!isCancelled)} />}
                    label={l10n.getString("production-order-show-cancelled")}
                />
            </AccountingFiltersDrawer>
        </>
    )
}
